<template>
    <section class="open-hours-services">
        <div class="inner">
            <template v-if="station.services.length > 0">
                <h2 class="h5">{{ $t('services') }}</h2>
                <div class="services">
                    <div
                        v-for="(item, key) in computedServices"
                        :key="`item-${key}`"
                        class="service-item"
                    >
                        <DSIcon v-if="item.icon.code" :code="`st1website-${item.icon.style}/${item.icon.code}`" size="l" />
                        <span>{{ $t(`stationAttributes.${item.title}`) }}</span>
                    </div>
                </div>
            </template>
            <template v-if="station.fuels.length > 0">
                <h2 class="h5 fuels">{{ $t('fuels') }}</h2>
                <div class="services">
                    <div
                        v-for="(item, key) in computedFuels"
                        :key="`item-${key}`"
                        class="service-item"
                    >
                        <DSIcon v-if="item.icon.code" :code="`st1website-fill/${item.icon.code}`" size="l" />
                        <span>{{ $t(`stationAttributes.${item.title}`) }}</span>
                    </div>
                </div>
            </template>
        </div>
    </section>
</template>
<script setup>
import { computed, useI18n } from '#imports';
import { storeToRefs } from 'pinia';
import { usePageStore } from '~dsc/stores/cms/page';

defineProps({
    stations: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const { locale } = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            services: 'Palvelut',
            fuels: 'Polttoaineet',
        },
        'sv-SE': {
            services: 'Tjänster',
            fuels: 'Drivmedel',

        },
        'no-NO': {
            services: 'Tjenester',
            fuels: 'Drivstoff',
        },
    },
});
const { station } = storeToRefs(usePageStore());

const computedServices = computed(() => {
    const icons = {
        servicefueling: { icon: 'serviced-fueling', iconStyle: 'fill' },
        adblueprivatecars: { icon: 'ad-blue', iconStyle: 'fill' },
        adblue: { icon: 'ad-blue', iconStyle: 'fill' },
        carmaintenance: { icon: 'vehicle-maintenance', iconStyle: 'fill' },
        trailerrent: { icon: 'trailer', iconStyle: 'fill' },
        post: { icon: 'mail', iconStyle: 'fill' },
        matkahuolto: { icon: 'package-pickup', iconStyle: 'fill' },
        automatedmatkahuolto: { icon: 'package-pickup', iconStyle: 'fill' },
        sauna: { icon: 'sauna', iconStyle: 'fill' },
        shower: { icon: 'shower', iconStyle: 'fill' },
        truckfuelingpoint: { icon: 'truck', iconStyle: 'fill' },
        selfservicecarwash: { icon: 'car-wash-self-service', iconStyle: 'fill' },
        carwash: { icon: 'car-wash', iconStyle: 'fill' },
        perfectcarwash: { icon: 'car-wash', iconStyle: 'fill' },
        carwash24h: { icon: 'car-wash24', iconStyle: 'fill' },
        perfectcarwash24hours: { icon: 'car-wash24', iconStyle: 'fill' },
        selfservicepaymentterminal: { icon: 'payment-cards', iconStyle: 'fill' },
        burgerking: { icon: 'hamburger', iconStyle: 'fill' },
        kotipizza: { icon: 'pizza', iconStyle: 'fill' },
        lunch: { icon: 'restaurant-stations', iconStyle: 'fill' },
        weekendlunch: { icon: 'restaurant-stations', iconStyle: 'fill' },
        cafeteria: { icon: 'cafe', iconStyle: 'line' },
        driverspremium: { icon: 'hamburger', iconStyle: 'fill' },
        goldenrax: { icon: 'pizza', iconStyle: 'fill' },
        othershop: { icon: 'services', iconStyle: 'fill' },
        restaurantnotconcept: { icon: 'restaurant-stations', iconStyle: 'fill' },
        meetingroom: { icon: 'whiteboard', iconStyle: 'fill' },
        drivers: { icon: 'hamburger', iconStyle: 'fill' },
        freewifi: { icon: 'network', iconStyle: 'fill' },
        pizza: { icon: 'pizza', iconStyle: 'fill' },
        truck: { icon: 'truck', iconStyle: 'fill' },
        food: { icon: 'restaurant-stations', iconStyle: 'fill' },
        coffee: { icon: 'cafe', iconStyle: 'line' },
        boutique: { icon: 'services', iconStyle: 'fill' },
        fueling: { icon: 'refuel', iconStyle: 'fill' },
        laddstation: { icon: 'recharge', iconStyle: 'fill' },
        toilet: { icon: 'toilet', iconStyle: 'fill' },
        wifi: { icon: 'network', iconStyle: 'fill' },
        nestekaasu: { icon: 'bottled-gas', iconStyle: 'fill' },
        accessibletoilet: { icon: 'disability-toilet', iconStyle: 'fill' },
        changingtable: { icon: 'family-room', iconStyle: 'fill' },
        devicecharging: { icon: 'battery-charge', iconStyle: 'fill' },
        seatingarea: { icon: 'outdoor-seat', iconStyle: 'fill' },
        schenker: { icon: 'package-pickup', iconStyle: 'fill' },
        budbee: { icon: 'package-pickup', iconStyle: 'fill' },
        ups: { icon: 'package-pickup', iconStyle: 'fill' },
        helmisimpukka: { icon: 'restaurant-stations', iconStyle: 'fill' },
        helmisimpukkarestaurant: { icon: 'helmisimpukkarestaurant', iconStyle: 'fill' },
        veneasema: { icon: 'marine-depot', iconStyle: 'fill' },
        automatedposti: { icon: 'package', iconStyle: 'fill' },
        evCharging: { icon: 'recharge', iconStyle: 'fill' },
        twoqualitydiesel: { icon: 'refuel', iconStyle: 'fill' },
        dieselHVO: { icon: 'refuel', iconStyle: 'fill' },
        diesel20: { icon: 'refuel', iconStyle: 'fill' },
        diesel35: { icon: 'refuel', iconStyle: 'fill' },
        diesel38: { icon: 'refuel', iconStyle: 'fill' },
        re85: { icon: 'refuel', iconStyle: 'fill' },
        lbg: { icon: 'refuel', iconStyle: 'fill' },
        trucklengthunder14: { icon: 'crt-truck', iconStyle: 'fill' },
        trucklengthunder2525: { icon: 'crt-truck', iconStyle: 'fill' },
        trucklengthunder345: { icon: 'crt-truck', iconStyle: 'fill' },

    };

    const brandServices = ['welcomein', 'ploq', 'dirtcarwash'];
    const notBrandService = service => !brandServices.includes(service.title);
    const extraLocaleFilter = service => {
        if (locale.value === 'sv-SE') return !['coffee', 'othershop', 'butik-ploq', 'butik-welcomein'].includes(service.title);

        return true;
    };
    const serviceCodeToServiceObject = service => {
        const serviceName = service.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '');

        return {
            icon: {
                code: icons[serviceName]?.icon ?? 'approved',
                style: icons[serviceName]?.iconStyle ?? 'fill',
            },
            title: serviceName,
        };
    };

    return Array.from(new Set(station.value.services) ?? [])
        .map(parseName)
        .map(serviceCodeToServiceObject)
        .filter(notBrandService)
        .filter(extraLocaleFilter);
});

const computedFuels = computed(() => (Array.from(new Set(station.value.fuels) ?? [])
    .map(parseName)
    .map(item => ({
        icon: { code: 'refuel' },
        title: item,
    }))
));

function parseName(name) {
    return name.replace(/^finland\//, '').replace(/^sweden\//, '').replace(/^norway\//, '');
}
</script>
<script>
export default {
    name: 'StationsFunctionsServices',
};
</script>
<style scoped>
.h5.fuels {
  margin-top: 1.25rem;
}

.open-hours-services {
  margin-bottom: 2rem;
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.open-hours-services .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.open-hours-services .inner {
  margin-right: auto;
}
.open-hours-services .open-hours-item .row {
  display: flex;
  justify-content: space-between;
}
.open-hours-services .open-hours-item .row:not(:last-child) {
  margin-bottom: 0.5rem;
}
.open-hours-services .open-hours-item:not(:last-child) {
  margin-bottom: 1.5rem;
}
.open-hours-services .service-item {
  display: grid;
  align-content: center;
  grid-template-columns: 2rem auto;
  min-height: 2.25rem;
  padding: 0.25rem;
}
.open-hours-services .service-item .icon {
  display: inline-flex;
  align-items: center;
  color: var(--color-base-grey-800);
}
.open-hours-services .service-item span {
  line-height: 1.25em;
  display: inline-flex;
  align-self: stretch;
  align-content: center;
}
@media (min-width: 768px) {
  .open-hours-services .services {
    display: grid;
    grid-template-columns: minmax(350px, auto) 1fr;
    gap: 0 1rem;
    align-items: center;
    box-sizing: border-box;
  }
  .open-hours-services .services > :nth-child(odd) {
    border-right: 1px solid var(--color-base-grey-300);
    margin-right: 2rem;
    padding-right: 2rem;
  }
}
</style>
